import { apiRequest, apiRequestWithMeta, API_BASE } from './client'
import { useAuthStore } from '@/store/authStore'
import type {
  ActivityFilters,
  AnalyticsData,
  AuditEvent,
  DashboardStats,
  DocumentFilters,
  DocumentItem,
  HealthStatus,
  OrgDetail,
  Organization,
  PageMeta,
  PlatformUser,
  SecuritySummary,
  SignatureFilters,
  SignerItem,
  SearchResultsData,
  UserFilters,
} from '@/types/api'

export function getDashboard() {
  return apiRequest<DashboardStats>('/api/root/dashboard')
}

export function listActivity(filters: ActivityFilters) {
  return apiRequestWithMeta<AuditEvent[], PageMeta>('/api/root/activity', {
    params: filters as Record<string, string | number | undefined>,
  })
}

export function getActivityDetail(id: string) {
  return apiRequest<AuditEvent>(`/api/root/activity/${id}`)
}

export function listOrganizations(params: {
  search?: string
  status?: string
  page?: number
  pageSize?: number
}) {
  return apiRequestWithMeta<Organization[], PageMeta>('/api/root/organizations', { params })
}

export function getOrganization(id: string) {
  return apiRequest<OrgDetail>(`/api/root/organizations/${id}`)
}

export function listOrganizationUsers(id: string, userType?: string, page = 1, pageSize = 50) {
  return apiRequestWithMeta<PlatformUser[], PageMeta>(`/api/root/organizations/${id}/users`, {
    params: { type: userType, page, pageSize },
  })
}

export function listUsers(params: UserFilters) {
  return apiRequestWithMeta<PlatformUser[], PageMeta>('/api/root/users', {
    params: params as Record<string, string | number | undefined>,
  })
}

export function getUser(id: string) {
  return apiRequest<PlatformUser>(`/api/root/users/${id}`)
}

export function getSecuritySummary() {
  return apiRequest<SecuritySummary>('/api/root/security/events')
}

export function getAnalytics(range: '24h' | '7d' | '30d' | '90d' | 'all') {
  return apiRequest<AnalyticsData>('/api/root/analytics', {
    params: { range },
  })
}

export function getSystemHealth() {
  return apiRequest<HealthStatus>('/api/root/system/health')
}

export function listDocuments(params: DocumentFilters) {
  return apiRequestWithMeta<DocumentItem[], PageMeta>('/api/root/documents', {
    params: params as Record<string, string | number | undefined>,
  })
}

export function getDocument(id: string) {
  return apiRequest<DocumentItem>(`/api/root/documents/${id}`)
}

export async function downloadDocumentFile(docId: string, filename?: string): Promise<void> {
  const token = useAuthStore.getState().accessToken
  const targetUrl = `${API_BASE}/api/root/documents/${docId}/download`
  const res = await fetch(targetUrl, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!res.ok) {
    const json = await res.json().catch(() => null)
    throw new Error(json?.error?.message || `Download failed (HTTP ${res.status})`)
  }

  const blob = await res.blob()
  const disposition = res.headers.get('content-disposition')
  let suggestedName = filename || `document-${docId}.pdf`
  if (disposition && disposition.includes('filename=')) {
    const match = disposition.match(/filename=["']?([^"';]+)["']?/)
    if (match && match[1]) suggestedName = match[1]
  }

  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = suggestedName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export function listSignatures(params: SignatureFilters) {
  return apiRequestWithMeta<SignerItem[], PageMeta>('/api/root/signatures', {
    params: params as Record<string, string | number | undefined>,
  })
}

export function getSignature(id: string) {
  return apiRequest<SignerItem>(`/api/root/signatures/${id}`)
}

export function updateOrgStatus(id: string, status: string) {
  return apiRequest<{ id: string; status: string }>(`/api/root/organizations/${id}/status`, {
    method: 'PATCH',
    body: { status },
  })
}

export function updateUserStatus(id: string, status: string) {
  return apiRequest<{ id: string; status: string }>(`/api/root/users/${id}/status`, {
    method: 'PATCH',
    body: { status },
  })
}

export function updateDocStatus(id: string, status: string) {
  return apiRequest<{ id: string; status: string }>(`/api/root/documents/${id}/status`, {
    method: 'PATCH',
    body: { status },
  })
}

export function globalSearch(q: string) {
  return apiRequest<SearchResultsData>('/api/root/search', {
    params: { q },
  })
}
